import { Box, Button, IconButton, MenuItem, Paper, TextField, Typography, CircularProgress, Divider, Autocomplete, Stack } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useHistory } from 'react-router-dom';
import { Colors } from '../../../../Styles/Colors';
import ToastMsg from '../../../../Components/ToastMsg/ToastMsg';
import Loader from '../../../../Components/Loader';
import { Serviceurls } from '../../../../Utility/API/Serviceurls';
import RestAPIService from '../../../../Utility/Services/RestAPIService';
import { getCachevalue } from '../../../../Utility/Services/CacheProviderService';
import { CheckAccessFunc } from '../../../../Components/CommonFunctions/CommonFunctions';

// Same patient/admission keys the BE auto-populates (mirrors SUPPORTED_PLACEHOLDER_KEYS
// in front_office/consent_forms/services.py and SYSTEM_KEY_OPTIONS in ConsentFormAdd.js).
// Used to label the placeholders found in the configured HTML.
const SYSTEM_KEY_LABELS = {
    patient_name: 'Patient Name',
    patient_age: 'Patient Age',
    patient_gender: 'Patient Gender',
    uhid: 'UHID',
    father_or_spouse_name: 'Father / Spouse Name',
    relation_of_patient: 'Relation of Patient',
    dob: 'Date of Birth',
    mobile_number: 'Mobile Number',
    address: 'Address',
    ip_no: 'IP Number',
    bed: 'Ward / Bed',
    doctor_name: 'Doctor Name',
    hospital_name: 'Hospital Name',
    hospital_address: 'Hospital Address',
    diagnosis: 'Diagnosis / Complaints',
    current_date: "Today's Date",
}
const SYSTEM_KEYS = new Set(Object.keys(SYSTEM_KEY_LABELS))

// Per-key input type so the rendered TextField restricts what the user can
// enter. Anything not listed here defaults to free-text.
//   - 'date'   : native date picker; stored as DD-MM-YYYY to match the BE
//                auto-populate format from services.build_consent_context.
//   - 'name'   : free text, digits stripped — preserves '.', '-', "'" so
//                "Dr.", "O'Brien", "Smith-Jones" still work.
//   - 'tel'    : digits + the formatting characters phone numbers carry
//                ('+' country prefix, ' ' separators, '-', '(', ')').
//                Letters and other punctuation are stripped on input. Capped
//                at 20 chars so a paste accident can't blow up the field.
//   - 'select' : MUI Select rendered via TextField select prop; values
//                restricted to GENDER_OPTIONS.
//   - 'integer': digits only; for patient_age (calculate_age returns the
//                year count as an integer per laboratory.utils.calculate_age).
const SYSTEM_KEY_TYPES = {
    dob: 'date',
    current_date: 'date',
    patient_name: 'name',
    doctor_name: 'name',
    father_or_spouse_name: 'name',
    relation_of_patient: 'name',
    mobile_number: 'tel',
    patient_gender: 'select',
    patient_age: 'integer',
}

const GENDER_OPTIONS = ['Male', 'Female', 'Others']

const stripDigits = (val) => String(val ?? '').replace(/[0-9]/g, '')

// Phone-friendly subset: digits, '+', spaces, dashes, parentheses. Capped at
// 20 chars to keep paste accidents from filling the cell with junk.
const sanitizePhone = (val) => String(val ?? '').replace(/[^0-9+\-\s()]/g, '').slice(0, 20)

// Digits-only, capped at 3 chars. Patient age is bounded by human lifespan.
const sanitizeInteger = (val) => String(val ?? '').replace(/[^0-9]/g, '').slice(0, 3)

// "01-01-1980" (DD-MM-YYYY, backend / display format) -> "1980-01-01" (YYYY-MM-DD,
// the format <input type="date"> requires for its `value` attribute).
const ddmmyyyyToIsoDate = (val) => {
    if (!val) return ''
    const m = String(val).match(/^(\d{2})-(\d{2})-(\d{4})$/)
    if (m) return `${m[3]}-${m[2]}-${m[1]}`
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val // already ISO
    return ''
}

// "1980-01-01" -> "01-01-1980". The native date picker emits ISO; we store
// DD-MM-YYYY everywhere else so the BE rendering pipeline doesn't see two
// formats for the same field.
const isoDateToDdmmyyyy = (iso) => {
    if (!iso) return ''
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    return m ? `${m[3]}-${m[2]}-${m[1]}` : iso
}

// Derive the editable field list from the configured HTML — one entry per
// unique {{key}} placeholder, in the order they appear.
const derivePlaceholders = (html) => {
    if (!html) return []
    const seen = new Set()
    const out = []
    const re = /\{\{\s*([a-zA-Z_][\w]*)\s*\}\}/g
    let m
    while ((m = re.exec(html)) !== null) {
        const key = m[1]
        if (seen.has(key)) continue
        seen.add(key)
        out.push({
            key,
            label: SYSTEM_KEY_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            isSystemKey: SYSTEM_KEYS.has(key),
        })
    }
    return out
}

export default function CreateConsentForm() {
    const history = useHistory();

    const [formData, setFormData] = useState({
        errorMsg: false, errorMsgText: '',
        successMsg: false, successMsgText: '',
        isloader: false,
        templates: [],
        selectedTemplate: null,
        templateDetail: null,
        fieldValues: {},        // ALL field values (auto-populated + manual), all editable
        previewUrl: '',
        previewSrcDoc: '',      // HTML wrapper carrying <title> for print/save name
        previewLoader: false,
        issuing: false,
        patientData: {},
    })

    const buildPdfFilename = (templateName) => {
        const safe = (templateName || 'Consent_Form').replace(/[^\w\s-]+/g, '').trim().replace(/\s+/g, '_').slice(0, 80) || 'Consent_Form'
        const d = new Date()
        const dd = String(d.getDate()).padStart(2, '0')
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        return `${safe}_${dd}-${mm}-${d.getFullYear()}.pdf`
    }
    const escapeHtmlAttr = (s) => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
    const buildPreviewSrcDoc = (url, filename) => {
        const safe = escapeHtmlAttr(filename)
        return `<!doctype html><html><head><meta charset="utf-8"/><title>${safe}</title></head><body style="margin:0;padding:0"><iframe src="${url}#toolbar=1" style="position:fixed;inset:0;width:100%;height:100%;border:0" title="${safe}"></iframe></body></html>`
    }

    useEffect(() => {
        try {
            const patientData = JSON.parse(getCachevalue('IppatientData') || '{}')
            setFormData(prev => ({ ...prev, patientData }))
        } catch { /* empty */ }
        loadTemplates()
        return () => { if (formData.previewUrl) URL.revokeObjectURL(formData.previewUrl) }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const errorMessage = (msg) => setFormData(prev => ({ ...prev, errorMsg: true, errorMsgText: msg }))
    const msgClose = () => setFormData(prev => ({ ...prev, errorMsg: false, errorMsgText: '', successMsg: false, successMsgText: '' }))

    const commonDisable = CheckAccessFunc('front_office', 'In Patients', 'Consent Forms', null, 'Tab')?.editAccess

    const loadTemplates = async () => {
        try {
            const response = await RestAPIService.getAll(`${Serviceurls.CONSENT_TEMPLATE_LIST}?only_published=true`)
            setFormData(prev => ({ ...prev, templates: response?.data?.data || [] }))
        } catch (e) {
            errorMessage(e?.response?.data?.message || e.message)
        }
    }

    const loadTemplateDetail = async (template) => {
        if (!template?.id) {
            setFormData(prev => ({ ...prev, templateDetail: null, fieldValues: {}, previewUrl: '' }))
            return;
        }
        try {
            setFormData(prev => ({ ...prev, isloader: true }))
            const patientId = formData.patientData?.patient_id
            const ipAdmissionId = formData.patientData?.id || formData.patientData?.ip_admission_id
            const reqs = [
                RestAPIService.getAll(`${Serviceurls.CONSENT_TEMPLATE_DETAIL}${template.id}/`),
            ]
            if (patientId) {
                let ctxUrl = `${Serviceurls.CONSENT_FORM_PATIENT_CONTEXT}?patient_id=${patientId}`
                if (ipAdmissionId) ctxUrl += `&ip_admission_id=${ipAdmissionId}`
                reqs.push(RestAPIService.getAll(ctxUrl))
            }
            const responses = await Promise.all(reqs)
            const detail = responses[0]?.data?.data
            const patientCtx = responses[1]?.data?.data || {}

            // Only the placeholders that actually appear in the configured HTML
            // become editable fields. System keys take their default from the
            // patient context; manual keys start empty. All fields are editable.
            const placeholders = derivePlaceholders(detail?.extracted_html)
            const initial = {}
            for (const ph of placeholders) {
                if (ph.isSystemKey && patientCtx[ph.key] !== undefined) {
                    initial[ph.key] = patientCtx[ph.key] ?? ''
                } else {
                    initial[ph.key] = ''
                }
            }
            setFormData(prev => ({ ...prev, templateDetail: detail, fieldValues: initial, isloader: false }))
        } catch (e) {
            setFormData(prev => ({ ...prev, isloader: false }))
            errorMessage(e?.response?.data?.message || e.message)
        }
    }

    const handleSelectTemplate = (template) => {
        setFormData(prev => ({ ...prev, selectedTemplate: template, previewUrl: '' }))
        loadTemplateDetail(template)
    }

    const handleIssue = async () => {
        const { selectedTemplate, fieldValues, patientData } = formData;
        if (!selectedTemplate) { errorMessage('Select a consent form'); return; }
        const patientId = patientData?.patient_id
        const ipAdmissionId = patientData?.id || patientData?.ip_admission_id
        if (!patientId) { errorMessage('Patient context missing'); return; }

        try {
            setFormData(prev => ({ ...prev, issuing: true, previewLoader: true }))
            const payload = {
                template_id: selectedTemplate.id,
                patient_id: patientId,
                ip_admission_id: ipAdmissionId,
                field_values: fieldValues,
                _nonce: Date.now(),
            }
            const issueResp = await RestAPIService.create(payload, Serviceurls.CONSENT_FORM_ISSUE)
            const instance = issueResp?.data?.data
            // Fetch the rendered PDF as blob to display in iframe
            const pdfResp = await RestAPIService.PrintPDF(`${Serviceurls.CONSENT_FORM_PDF}${instance.id}/pdf/`)
            const blob = new Blob([pdfResp.data], { type: 'application/pdf' })
            const url = URL.createObjectURL(blob)
            const filename = pdfResp?.headers?.['x-consent-filename']
                || buildPdfFilename(selectedTemplate?.name)
            setFormData(prev => ({
                ...prev,
                issuing: false, previewLoader: false,
                previewUrl: url,
                previewSrcDoc: buildPreviewSrcDoc(url, filename),
                successMsg: true, successMsgText: 'Consent form saved',
            }))
        } catch (e) {
            setFormData(prev => ({ ...prev, issuing: false, previewLoader: false }))
            errorMessage(e?.response?.data?.message || e.message)
        }
    }

    const handleReset = () => {
        if (formData.selectedTemplate) {
            // Re-fetch the patient context + template so fields snap back to defaults
            loadTemplateDetail(formData.selectedTemplate)
        }
        setFormData(prev => ({ ...prev, previewUrl: '', previewSrcDoc: '' }))
    }

    const placeholders = useMemo(() => {
        return derivePlaceholders(formData.templateDetail?.extracted_html)
    }, [formData.templateDetail])

    const renderFormDetails = () => (
        <Box sx={{ height: '100%' }}>
            <Typography fontSize={'1vw'} fontWeight={'bold'} padding={'0.5vw'}>Form Details</Typography>
            <Divider />
            <Box sx={{ p: 1, overflowY: 'auto', height: 'calc(100% - 40px)' }}>
                {!formData.selectedTemplate && (
                    <Typography color="text.secondary" fontSize="0.85vw">Select a consent form above to begin.</Typography>
                )}
                {formData.selectedTemplate && placeholders.length > 0 && (
                    <Stack spacing={1.2} sx={{ mt: 0.5 }}>
                        {placeholders.map(ph => {
                            const fieldType = SYSTEM_KEY_TYPES[ph.key]
                            const stored = formData.fieldValues[ph.key] ?? ''
                            // Date inputs need the value in YYYY-MM-DD; we store
                            // DD-MM-YYYY, so translate at the input boundary.
                            const inputValue = fieldType === 'date' ? ddmmyyyyToIsoDate(stored) : stored
                            const onChange = (e) => {
                                let v = e.target.value
                                if (fieldType === 'date') v = isoDateToDdmmyyyy(v)
                                else if (fieldType === 'name') v = stripDigits(v)
                                else if (fieldType === 'tel') v = sanitizePhone(v)
                                else if (fieldType === 'integer') v = sanitizeInteger(v)
                                setFormData(prev => ({
                                    ...prev,
                                    fieldValues: { ...prev.fieldValues, [ph.key]: v },
                                }))
                            }
                            if (fieldType === 'select') {
                                return (
                                    <TextField key={ph.key} select size="small" label={ph.label} fullWidth
                                        value={GENDER_OPTIONS.includes(stored) ? stored : ''}
                                        onChange={onChange}>
                                        {GENDER_OPTIONS.map(opt => (
                                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                        ))}
                                    </TextField>
                                )
                            }
                            return (
                                <TextField key={ph.key} size="small" label={ph.label} fullWidth
                                    type={fieldType === 'date' ? 'date' : 'text'}
                                    value={inputValue}
                                    InputLabelProps={fieldType === 'date' ? { shrink: true } : undefined}
                                    inputProps={fieldType === 'tel'
                                        ? { inputMode: 'tel', maxLength: 20 }
                                        : fieldType === 'integer'
                                            ? { inputMode: 'numeric', maxLength: 3 }
                                            : undefined}
                                    onChange={onChange} />
                            )
                        })}
                    </Stack>
                )}
                {formData.selectedTemplate && !placeholders.length && (
                    <Typography color="text.secondary" fontSize="0.85vw">This consent form has no fillable fields.</Typography>
                )}
            </Box>
        </Box>
    )

    return (
        <Box sx={{ height: '85vh', width: '94vw', backgroundColor: Colors.Background, padding: '.5vw' }}>
            <Box height={'5vh'} display={'flex'} alignItems={'center'} sx={{ backgroundColor: 'white', borderBottom: '1px solid lightgray' }}>
                <IconButton onClick={() => history.push('/IPHome/PatientDetails/ConsentForms')}>
                    <ChevronLeftIcon color='primary' />
                </IconButton>
                <Typography color={Colors.SecondaryText} fontWeight={600} fontSize={'0.95vw'}>Back To IP Consent Forms</Typography>
            </Box>

            <Box sx={{ height: '8vh', width: '93vw', backgroundColor: 'white', marginTop: '.8vw' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '8vh', paddingLeft: '0.5vw' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '0.5vw' }}>
                        <Autocomplete
                            size='small'
                            options={formData.templates}
                            getOptionLabel={(o) => o.name || ''}
                            value={formData.selectedTemplate}
                            sx={{ width: '20vw' }}
                            onChange={(_, newValue) => handleSelectTemplate(newValue)}
                            renderInput={(params) => <TextField {...params} label='Select Consent Form' />}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', paddingRight: '1vw' }}>
                        <Button variant='outlined' size='small' sx={{ textTransform: 'capitalize', mr: 1, width: '6vw' }}
                            onClick={handleReset}>Reset</Button>
                        <Button variant='contained' size='small' sx={{ textTransform: 'capitalize', width: '9vw' }}
                            onClick={handleIssue} disabled={formData.issuing || !formData.selectedTemplate || !commonDisable}>
                            {formData.issuing ? 'Saving…' : 'Save & Preview'}
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ height: '68vh', display: 'flex', gap: '1vw', marginTop: '.8vw' }}>
                <Paper elevation={2} sx={{ flex: '0 0 72%', maxWidth: '72%', display: 'flex', flexDirection: 'column' }}>
                    <Typography className='eMed_refdoc_title' sx={{ padding: '0.625vw', borderBottom: '1px solid lightGray' }}>
                        Print Preview
                    </Typography>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflowY: 'auto' }}>
                        {formData.previewLoader
                            ? <CircularProgress color='primary' size={'2vw'} />
                            : formData.previewUrl
                                ? <iframe style={{ width: '100%', height: '100%', border: 'none' }}
                                    srcDoc={formData.previewSrcDoc}
                                    src={formData.previewSrcDoc ? undefined : `${formData.previewUrl}#toolbar=1`}
                                    title="Consent PDF" />
                                : <Typography fontSize={'1vw'}>Save the form to see the printed preview</Typography>}
                    </Box>
                </Paper>
                <Paper elevation={2} sx={{ flex: '0 0 26%', maxWidth: '26%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                        {renderFormDetails()}
                    </Box>
                </Paper>
            </Box>

            {formData.errorMsg && <ToastMsg severity={'error'} msg={formData.errorMsgText} msgPop={msgClose} />}
            {formData.successMsg && <ToastMsg severity={'success'} msg={formData.successMsgText} msgPop={msgClose} />}
            <Loader loaderOpen={formData.isloader} />
        </Box>
    )
}
